<template>
  <b-container fluid>
    <h1 class="m-3">
      {{ title }}
    </h1>
    <b-row class="px-3 justify-content-between">
      <b-col cols="12">
        <div class="d-flex flex-wrap flex-lg-nowrap filter-gap">

          <div class="filter-width">
            <filter-site-picker :use-default-value="true" :multiple="true" :select-all="true" operator="equals"
              :item-key="siteIdFilterProperty" :item-label="siteLabelFilterProperty"
              :item-currency="siteCurrencyFilterProperty" :group-by-key="groupKey" :group-by-label="groupLabel"
              @filterWithIdChanged="siteFilterChanged($event)" />
          </div>

          <div class="d-flex filter-gap flex-wrap">
            <date-range-dual-picker :time-range-value="timeRangeValue"
              @dateRangeDualFiltersUpdated="dateRangeDualFiltersUpdated($event)" />

            <time-range-picker :use-default-value="true" @timeRangeFilterChanged="timeRangeFilterUpdated($event)" />
          </div>

          <b-dropdown toggle-class="dropdown-style" right :text="$t('export.export')"
            class="ml-lg-auto align-self-center align-self-lg-start">
            <b-dropdown-item class="mb-2" :disabled="!dataReadyTables" @click="downloadPdf()">
              <font-awesome-icon icon="file-pdf" class="mr-2 export-icon-pdf" />
              {{ $t("export.PDF") }}
            </b-dropdown-item>
            <b-dropdown-item class="mb-2" :disabled="!dataReadyTables" @click="downloadExcel()">
              <font-awesome-icon icon="file-excel" class="mr-2 export-icon-excel" />
              {{ $t("export.excel") }}
            </b-dropdown-item>
            <b-dropdown-item :disabled="!dataReadyTables" @click="downloadCsv()">
              <font-awesome-icon icon="file-csv" class="mr-2 export-icon" />
              {{ $t("export.csv") }}
            </b-dropdown-item>
          </b-dropdown>

        </div>
      </b-col>
    </b-row>
    <table-renderer v-for="data of tablesData" :key="data.tableNumber" :data-table="data.dataTable" ref="tableRenderers"
      :dimension-fields="data.dimensionColumns" :measure-fields="data.measureColumns" :group-headers="groupHeaders"
      :table-header="data.tableHeader" :totals-data="data.totalsData" :data-ready="dataReadyTables"
      custom-table-class="table-width-medium" />
  </b-container>
</template>

<script>
import Tools from "@/utils/tools";
import TableTools from "@/utils/tableTools";
import paths from "@/router/pathConstants";
import TableRenderer from "@/components/TableRenderer.vue";
import mesureEnum from "@/model/mesureEnum";
import dimensionEnum from "@/model/dimensionEnum";
import {
  columnTypeEnum,
  groupKeyEnum,
  calculatedColumnsEnum,
} from "@/model/columnsEnums";
import { OperationsCompare } from "@/router/routeNames";
import { dateFormatEnum } from "@/filter/dateFormat";
import FilterSitePicker from "@/components/picker/FilterSitePicker.vue";
import DateRangeDualPicker from "@/components/picker/DateRangeDualPicker.vue";
import TimeRangePicker from "@/components/picker/TimeRangePicker.vue";
import _ from "lodash";
import abortMixin from "@/mixins/abortMixin";

export default {
  name: OperationsCompare,
  mixins: [abortMixin],
  components: {
    FilterSitePicker,
    DateRangeDualPicker,
    TimeRangePicker,
    TableRenderer
  },
  data() {
    return {
      /** Tableau d'objets des sites sélectionnés, indiquant leur id et leur label */
      siteValues: [],
      /** Filtre venant du FilterPicker, tel qu'attendu par cubeJs.
       * Ex :
       *    {
       *      dimension: "Site.label",
       *      operator: "equals",
       *      values: ["Site 1"]
       *    }
       */
      siteFilter: null,
      siteIdFilterProperty: dimensionEnum.SITE,
      siteLabelFilterProperty: dimensionEnum.SITE_NAME,
      siteCurrencyFilterProperty: dimensionEnum.SITE_CURRENCY,
      groupKey: dimensionEnum.SITE_SITE_CATEGORY,
      groupLabel: dimensionEnum.SITE_SITE_CATEGORY_NAME,
      datePickerFormat: dateFormatEnum.DATE_SHORT_FORMAT,
      /** Filtre venant du DateRangePicker, tel qu'attendu par cubeJs.
       * Ex :
       *    {
       *      dimension: "BusinessDate.id",
       *      operator: "inDateRange",
       *      values: ["2020-09-23", "2020-09-26"]
       *    }
       */
      dateRangeFilter1: null,
      dateRangeFilter2: null,
      /** Libellé des dates sélectionnées, telles qu'affichées dans le DateRangePicker */
      dateRangeStr1: "",
      dateRangeStr2: "",
      /** Libellé de la période (dates) sélectionnée */
      labelDatePeriod1: "",
      labelDatePeriod2: "",
      /** Filtre venant du TimeRangePicker, tel qu'attendu par cubeJs.
       * NB : Il faut lister les valeurs possibles au lieu d'utiliser des filtres >,< car cubeJs ne supporte pas encore bien le OR (pour les horaires > 23)
       * Ex :
       *    {
       *      dimension: dimensionEnum.TIME_HOUR,
       *      operator: "equals",
       *      values: ["18", "19", "20", "21", "22", "23", "0", "1"]
       *    }
       */
      timeRangeFilter: null,
      isTimeFullRange: false,
      timeRangeValue: null,
      dataReadyTables: false,
      tablesData: [],
      groupHeaders: [],
    }
  },
  computed: {
    isDefaultQueryReady() {
      return (
        this.siteFilter &&
        this.dateRangeFilter1 &&
        this.dateRangeFilter2 &&
        (this.timeRangeFilter || this.isTimeFullRange)
      );
    },
    title() {
      return this.$t("title.operations_compare");
    }
  },
  mounted() {
  },
  methods: {
    siteFilterChanged(value) {
      this.siteValues = value.values;
      this.siteFilter = value.idFilter;
      this.updateTables();
    },
    dateRangeDualFiltersUpdated(value) {
      if (value && value.dateRangePickerData1 && value.dateRangeFilterData2) {
        this.dateRangeFilter1 = value.dateRangePickerData1.dateRangeFilter;
        this.dateRangeStr1 = value.dateRangePickerData1.dateRangeStr;
        this.labelDatePeriod1 = value.dateRangePickerData1.labelPeriod;
        this.dateRangeFilter2 = value.dateRangeFilterData2.dateRangeFilter;
        this.dateRangeStr2 = value.dateRangeFilterData2.dateRangeStr;
        this.labelDatePeriod2 = value.dateRangeFilterData2.labelPeriod;
      } else {
        this.dateRangeFilter1 = null;
        this.dateRangeStr1 = "";
        this.labelDatePeriod1 = "";
        this.dateRangeFilter2 = null;
        this.dateRangeStr2 = "";
        this.labelDatePeriod2 = "";
      }
      this.updateTables();
    },
    timeRangeFilterUpdated(value) {
      if (value) {
        this.timeRangeValue = value;
        this.timeRangeFilter = value.timeRangeFilter;
        this.isTimeFullRange = value.isFullRange;
      } else {
        this.timeRangeValue = null;
        this.timeRangeFilter = null;
        this.isTimeFullRange = false;
      }
      this.updateTables();
    },
    updateTables() {
      if (this.isDefaultQueryReady) {
        let commonFilters = [this.siteFilter];
        if (!this.isTimeFullRange) {
          commonFilters.push(this.timeRangeFilter);
        }
        const dimensions = [
          dimensionEnum.SITE,
          dimensionEnum.SITE_NAME,
        ];
        this.updateTableList(
          commonFilters,
          this.dateRangeFilter1,
          this.dateRangeFilter2,
          dimensions
        );
      } else {
        this.dataReadyTables = false;
      }
    },
    async updateTableList(
      filters,
      dateRangeFilter1,
      dateRangeFilter2,
      dimensions
    ) {
      try {
        const { signal } = this.resetAbortController();
        var self = this;
        self.dataReadyTables = false;

        self.tablesData = [];
        // Initialisation des objets contenant toutes les données pour chaque site :
        // permet d'afficher dès maintenant les entêtes de tableaux et le loader pour indiquer que les requêtes sont en cours
        self.siteValues.forEach((site, i) => {
          var table = {
            tableNumber: i.toString(),
            tableHeader: site.label,
            tableHeaderId: site.id,
            dataTable: [],
            totalsData: {},
          };
          self.tablesData.push(table);
        });

        let gamesFilters = [...filters];
        gamesFilters.push(TableTools.Helper.getFilterGameNotFree());

        var queriesPeriode1 = self.getQueries(dateRangeFilter1, filters, gamesFilters, dimensions);
        var queriesPeriode2 = self.getQueries(dateRangeFilter2, filters, gamesFilters, dimensions);

        var resultSetsPeriode1 = await TableTools.Helper.doMultipleRequest(queriesPeriode1, { signal });
        var resultSetsPeriode2 = await TableTools.Helper.doMultipleRequest(queriesPeriode2, { signal });
        // réalisation d'un premier merge des données pour les regrouper
        // en type d'exploitation comme dans OperationDaily.vue
        var mergedResultPeriode1 = self.mergeAndCompleteResultsTable(resultSetsPeriode1);
        var mergedResultPeriode2 = self.mergeAndCompleteResultsTable(resultSetsPeriode2);

        // Extraire les devises depuis les résultats pour chaque site
        const siteCurrencies = {};
        [...mergedResultPeriode1, ...mergedResultPeriode2].forEach(result => {
          const siteId = result[dimensionEnum.SITE];
          if (siteId && result[dimensionEnum.SITE_CURRENCY] && !siteCurrencies[siteId]) {
            siteCurrencies[siteId] = result[dimensionEnum.SITE_CURRENCY];
          }
        });

        // Assigner les devises aux tables
        self.tablesData.forEach(table => {
          table.siteCurrency = siteCurrencies[table.tableHeaderId] || 'EUR';
        });

        // formation d'un tableau tel qu'attendu par mergeResultsByCommonDimensions
        // Cela simule une réponse d'une querie cube
        // Permet d'utiliser la méthode de TableTools.Helper au lieu de la dupliquer
        // -------------------------------------------------------------------------
        // Cela permet de merge une deuxieme fois pour avoir les périodes dans les key
        // ex : calculatedColumn.revenueHt##hiddenGroup.period2
        const resultBeforeMerging = self.formatingForMerging(mergedResultPeriode1, mergedResultPeriode2);

        const dimensionToUseForMerging = [
          dimensionEnum.SITE,
          dimensionEnum.SITE_NAME,
          calculatedColumnsEnum.TAXES_PERCENTAGE,
          calculatedColumnsEnum.SALE_CATEGORY
        ];
        // résultat du deuxième merge, les données sont prêtes à être affichées
        const finalMergeResult = TableTools.Helper.mergeResultsByCommonDimensions(resultBeforeMerging, dimensionToUseForMerging)

        // Initialiser les groupHeaders
        self.groupHeaders = [
          { nbOfMeasures: 3, label: self.$t("group.invoiceAmountHt") },
          { nbOfMeasures: 3, label: self.$t("group.invoiceAmount") },
        ];

        // Complétion des objets avec les données reçues pour chaque site
        self.siteValues.forEach((site) => {
          var table = self.tablesData.find(
            (tab) => tab.tableHeaderId === site.id
          );
          table.dataTable = finalMergeResult.filter(
            (result) => result[dimensionEnum.SITE] === site.id
          );

          // Utiliser la devise du site spécifique
          const tableSiteCurrency = table.siteCurrency || 'EUR';

          // Ajouter la devise à chaque ligne pour que TableRenderer la récupère automatiquement
          table.dataTable.forEach(row => {
            row["Site.currency"] = tableSiteCurrency;
          });

          // Créer les colonnes avec la devise du site
          table.dimensionColumns = TableTools.Helper.constructColumnTable(
            [calculatedColumnsEnum.SALE_CATEGORY],
            null,
            columnTypeEnum.DIMENSION,
            true,
            null
          );
          table.measureColumns = TableTools.Helper.constructColumnTable(
            [
              calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD1,
              calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD2,
              calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT,
              calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1,
              calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2,
              calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS,
            ],
            null,
            columnTypeEnum.MEASURE,
            true,
            null,
            false,
            tableSiteCurrency
          );

          table.measureColumns = table.measureColumns.map((col) => {
            if (
              col.key == calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS ||
              col.key == calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT
            ) {
              col.tdClass = "col-align-left";
            }
            return col;
          });

          // Modification des labels de périodes pour ce tableau
          table.measureColumns.forEach((mesureColmun) => {
            if (
              mesureColmun.key == calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1 ||
              mesureColmun.key == calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD1
            ) {
              mesureColmun.label = TableTools.Helper.formatDateLabel(dateRangeFilter1.dateRange);
            }
            else if (
              mesureColmun.key == calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2 ||
              mesureColmun.key == calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD2
            ) {
              mesureColmun.label = TableTools.Helper.formatDateLabel(dateRangeFilter2.dateRange);
            }
          });
          table.dataTable = self.completeInvoiceGroup(table.dataTable);
          table.totalsData = TableTools.Helper.calculateTotalsData(
            table.dataTable,
            [
              calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1,
              calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2,
              calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD1,
              calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD2,
            ]
          );
          table.dataTable = self.completePercentageGroupAndAddStyles(
            table.dataTable,
            table.totalsData
          );
          table.totalsData = self.completeTotals(table.totalsData);
        });

        self.dataReadyTables = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        Tools.Alerts.Error(this.$swal, error);
      }
    },
    getQueries(dateRangeFilter, filters, gamesFilters, dimensions) {
      let queries = [];

      queries.push({
        groupKey: groupKeyEnum.GROUP_GAMES,
        query: {
          measures: [
            mesureEnum.GAMEHISTORY_TOKEN_PRICE,
            mesureEnum.GAMEHISTORY_TOKEN_PRICE_HT,
          ],
          dimensions: [...dimensions, dimensionEnum.GAMEHISTORY_TAXESPERCENT, dimensionEnum.SITE_CURRENCY],
          timeDimensions: dateRangeFilter ? [dateRangeFilter] : [],
          filters: [...gamesFilters],
        },
      });

      queries.push({
        groupKey: groupKeyEnum.GROUP_SNACK,
        query: {
          measures: [
            mesureEnum.SNACKHISTORY_TOTALTOKEN_WITHOUTCB,
            mesureEnum.SNACKHISTORY_TOKEN_PRICE,
            mesureEnum.SNACKHISTORY_TOKEN_PRICE_HT,
            mesureEnum.SNACKHISTORY_AMOUNT_CB_TTC,
          ],
          dimensions: [
            ...dimensions,
            dimensionEnum.SNACKHISTORY_TAXESPERCENT,
            dimensionEnum.SNACKPRODUCT_TYPE,
            dimensionEnum.SNACKHISTORY_ISPAYMENTCB,
          ],
          timeDimensions: dateRangeFilter ? [dateRangeFilter] : [],
          filters: [...filters],
        },
      });

      return queries;
    },
    //
    /** Concaténation des données, renommage des clés pour les données qui doivent apparaître dans la même colonne,
    * ajout de la colonne de libellés et des colonnes calculées  */
    mergeAndCompleteResultsTable(resultSets) {
      var self = this;
      var concatenatedResults = [];
      let totalRevenue = 0;

      resultSets.forEach((set) => {
        //On groupe par type et type de payement (pour les types de conso)
        const groupByType = _.groupBy(
          set.resultSet.loadResponse.results[0].data,
          (value) => {
            // On verifie si on est bien avec des produits Snack/Merch (pas de jeux)
            // et si c'est le cas, on créé la clé de groupement : type-de-produit + type-payement
            return dimensionEnum.SNACKPRODUCT_TYPE in value
              ? [
                value[dimensionEnum.SNACKPRODUCT_TYPE],
                value[dimensionEnum.SNACKHISTORY_ISPAYMENTCB],
              ]
              : undefined;
          }
        );
        Object.keys(groupByType).forEach((key) => {
          var results = groupByType[key],
            groupKey = set.groupKey;
          if (key !== "undefined") {
            groupKey += key;
          }

          results.forEach((result) => {
            if (groupKey == groupKeyEnum.GROUP_GAMES) {
              result[calculatedColumnsEnum.SALE_CATEGORY] = self.$t("group.gameDynamicTaxes").format(
                result[dimensionEnum.GAMEHISTORY_TAXESPERCENT]
              )
            } else {
              result[calculatedColumnsEnum.SALE_CATEGORY] = self.$t(groupKey);
            }

            TableTools.Helper.renameKey(
              result,
              mesureEnum.GAMEHISTORY_TOKEN,
              calculatedColumnsEnum.TOKEN_QUANTITY
            );
            TableTools.Helper.renameKey(
              result,
              mesureEnum.SNACKHISTORY_TOTALTOKEN_WITHOUTCB,
              calculatedColumnsEnum.TOKEN_QUANTITY
            );
            TableTools.Helper.renameKey(
              result,
              mesureEnum.SNACKHISTORY_TOKEN_PRICE,
              calculatedColumnsEnum.REVENUE_TTC
            );

            TableTools.Helper.renameKey(
              result,
              mesureEnum.GAMEHISTORY_TOKEN_PRICE,
              calculatedColumnsEnum.REVENUE_TTC
            );

            TableTools.Helper.renameKey(
              result,
              mesureEnum.SNACKHISTORY_TOKEN_PRICE_HT,
              calculatedColumnsEnum.REVENUE_HT
            );

            TableTools.Helper.renameKey(
              result,
              mesureEnum.GAMEHISTORY_TOKEN_PRICE_HT,
              calculatedColumnsEnum.REVENUE_HT
            );

            TableTools.Helper.renameKey(
              result,
              dimensionEnum.SNACKHISTORY_TAXESPERCENT,
              calculatedColumnsEnum.TAXES_PERCENTAGE
            );
            TableTools.Helper.renameKey(
              result,
              dimensionEnum.GAMEHISTORY_TAXESPERCENT,
              calculatedColumnsEnum.TAXES_PERCENTAGE
            );
            TableTools.Helper.renameKey(
              result,
              mesureEnum.GAMEHISTORY_BONUSTOKEN,
              calculatedColumnsEnum.BONUS_TOKEN_QUANTITY
            );

            totalRevenue += result[calculatedColumnsEnum.REVENUE_TTC] || 0;

            concatenatedResults.push(result);
          });
        });
      });

      concatenatedResults.forEach((result) => {
        const revenue = result[calculatedColumnsEnum.REVENUE_TTC] || 0;
        result[calculatedColumnsEnum.PERCENT_PER_ACTIVITY] =
          (revenue * 100) / totalRevenue || 0;
      });

      return concatenatedResults;
    },
    /** Formatte les données pour former un tableau d'entrée comme attendu dans mergeResultsByCommonDimensions
     * Cela simule une réponse d'une requête cube js
     * Nécéssaire car un traitement a déja été fait sur les resultSets des requetes
    */
    formatingForMerging(mergedResultPeriode1, mergedResultPeriode2) {
      return [
        {
          groupKey: groupKeyEnum.HIDDENGROUP_PERIOD1,
          resultSet: {
            loadResponse: {
              results: [
                { data: mergedResultPeriode1 }
              ]
            }
          }
        },
        {
          groupKey: groupKeyEnum.HIDDENGROUP_PERIOD2,
          resultSet: {
            loadResponse: {
              results: [
                { data: mergedResultPeriode2 }
              ]
            }
          }
        }
      ];
    },
    completeInvoiceGroup(dataTable) {
      return dataTable.map((data) => {
        // Recette TTC
        const purchaseAmount1 =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            calculatedColumnsEnum.REVENUE_TTC,
            groupKeyEnum.HIDDENGROUP_PERIOD1
          )
          ];
        const revenue1 = purchaseAmount1 || 0;
        data[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1] = revenue1;

        const purchaseAmount2 =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            calculatedColumnsEnum.REVENUE_TTC,
            groupKeyEnum.HIDDENGROUP_PERIOD2
          )
          ];
        const revenue2 = purchaseAmount2 || 0;
        data[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2] = revenue2;

        data[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS] = revenue2
          ? (revenue1 / revenue2 - 1) * 100
          : this.$t("Display.notSignificant");

        // Recette HT
        const purchaseAmountHt1 =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            calculatedColumnsEnum.REVENUE_HT,
            groupKeyEnum.HIDDENGROUP_PERIOD1
          )
          ];
        const revenueHt1 = purchaseAmountHt1 || 0;
        data[calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD1] = revenueHt1;

        const purchaseAmountHt2 =
          data[
          TableTools.Helper.formatKeyWithGroupKey(
            calculatedColumnsEnum.REVENUE_HT,
            groupKeyEnum.HIDDENGROUP_PERIOD2
          )
          ];
        const revenueHt2 = purchaseAmountHt2 || 0;
        data[calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD2] = revenueHt2;

        data[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT] = revenueHt2
          ? (revenueHt1 / revenueHt2 - 1) * 100
          : this.$t("Display.notSignificant");

        return data;
      });
    },
    completePercentageGroupAndAddStyles(dataTable, totalsData) {
      return dataTable.map((data) => {
        const revenue1 =
          data[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1] || 0;
        const totalRevenue1 =
          totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1] || 0;
        const percentPeriod1 = totalRevenue1
          ? (revenue1 * 100) / totalRevenue1
          : 0;
        data[calculatedColumnsEnum.PERCENT_PER_OPERATION_PERIOD1] = percentPeriod1;

        const revenue2 =
          data[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2] || 0;
        const totalRevenue2 =
          totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2] || 0;
        const percentPeriod2 = totalRevenue2
          ? (revenue2 * 100) / totalRevenue2
          : 0;
        data[calculatedColumnsEnum.PERCENT_PER_OPERATION_PERIOD2] = percentPeriod2;

        data[calculatedColumnsEnum.COMPARE_REVENUE_PERCENT] =
          percentPeriod1 && percentPeriod2
            ? percentPeriod1 - percentPeriod2
            : this.$t("Display.notSignificant");

        return this.addProgressionStyles(data);
      });
    },
    completeTotals(totalsData) {
      const totalRevenue1 =
        totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD1] || 0;
      const totalRevenue2 =
        totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_PERIOD2] || 0;
      totalsData[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS] = totalRevenue2
        ? (totalRevenue1 / totalRevenue2 - 1) * 100
        : this.$t("Display.notSignificant");

      const totalRevenueHt1 =
        totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD1] || 0;
      const totalRevenueHt2 =
        totalsData[calculatedColumnsEnum.INVOICE_AMOUNT_HT_PERIOD2] || 0;
      totalsData[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT] = totalRevenueHt2
        ? (totalRevenueHt1 / totalRevenueHt2 - 1) * 100
        : this.$t("Display.notSignificant");

      totalsData["column-class"] = {};
      totalsData["column-class"][
        calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS
      ] = "col-align-left";
      totalsData["column-class"][
        calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT
      ] = "col-align-left";

      return this.addProgressionStyles(totalsData);
    },
    addProgressionStyles(obj) {
      var cellVariants = {};

      const invoiceProgression =
        obj[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS];
      if (invoiceProgression && !isNaN(invoiceProgression)) {
        cellVariants[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS] =
          invoiceProgression > 0 ? "success" : "danger";
      }

      const percentageProgression =
        obj[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT];
      if (percentageProgression && !isNaN(percentageProgression)) {
        cellVariants[calculatedColumnsEnum.COMPARE_INVOICE_AMOUNTS_HT] =
          percentageProgression > 0 ? "success" : "danger";
      }

      obj["_cellVariants"] = cellVariants;
      return obj;
    },
    downloadPdf() {
      if (this.dataReadyTables) {
        this.storeExportData();
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel() {
      if (this.dataReadyTables) {
        this.storeExportData();
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv() {
      if (this.dataReadyTables) {
        this.storeExportData();
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData() {
      const self = this;
      var datasForExport = [];

      self.tablesData.forEach((table, index) => {
        const renderer = self.$refs.tableRenderers?.[index];
        const sortKey = renderer?.sortedColumn || self.defaultSortedColumnTables;

        const data = TableTools.Helper.assembleExportDataTable(
          table.dataTable,
          table.dimensionColumns,
          table.measureColumns,
          self.groupHeaders,
          table.tableHeader,
          sortKey,
          null,
          table.totalsData
        );
        datasForExport.push(data);
      });

      const siteLabels = self.siteValues.map((site) => site.label);

      TableTools.Helper.storeExportData(
        datasForExport,
        self.title,
        Tools.StringHelper.constructExportSubtitles(
          siteLabels,
          self.labelDatePeriod1,
          "",
          self.labelDatePeriod2
        ),
        Tools.StringHelper.constructExportFilename(
          self.title,
          null,
          self.dateRangeStr1
        )
      );
    },
  },
}
</script>
